<script>
import axios from "axios";
import { nextTick } from "vue";
import YoutubeVideo from "@/components/VideoPlayer.vue";
import AudioPlayer from "@/components/AudioPlayer.vue";
import HawamPlayer from "@/components/HawamPlayer.vue";
import PdfLearningViewer from "@/components/pdf/PdfLearningViewer.vue";
import youtubeIcon from "@/assets/Icons/youtube.vue";
import pdf from "@/assets/Icons/pdf.vue";
import audio from "@/assets/Icons/audio.vue";
import { useAcademicYears } from "@/composables/useAcademicYears";

export default {
  name: "contents",
  components: { AudioPlayer, YoutubeVideo, HawamPlayer, PdfLearningViewer, youtubeIcon, pdf, audio },
  setup() {
    const { getAcademicYearName, fetchAcademicYears } = useAcademicYears();
    return { getAcademicYearName, fetchAcademicYears };
  },
  data() {
    return {
      education_level: JSON.parse(localStorage.getItem("user")).education_level,
      user: JSON.parse(localStorage.getItem("user") || "{}"),
      chapters: [],
      lessons: [],
      content: {},
      videoId: "",
      openedChapterId: null,
      openedLessonId: null,
      selectedContentId: null,
      activePlayer: "youtube",
      sidebarOpen: true,
      isMobile: window.innerWidth <= 768,
      // Subscription gate (Feature 7): set when the API returns 403
      // subscription_required, so the page shows a locked state with a
      // purchase CTA instead of an empty list. Backend enforcement is
      // authoritative — this is just the UI.
      subscriptionLocked: false,
      subscriptionMessage: "للوصول إلى محتوى الحصص، يجب الاشتراك في هذه السنة الدراسية.",
    };
  },
  computed: {
    academicYearLabel() {
      return this.getAcademicYearName(this.user?.academic_year_id);
    },
  },
  methods: {
    // True when the API refused a request because the student has no active
    // subscription for this academic year (SubscriptionGate 403).
    isSubscriptionError(e) {
      const r = e && e.response;
      return !!r && r.status === 403 && r.data && r.data.code === "subscription_required";
    },
    handleSubscriptionError(e) {
      if (this.isSubscriptionError(e)) {
        this.subscriptionLocked = true;
        if (e.response.data.message) this.subscriptionMessage = e.response.data.message;
        return true;
      }
      return false;
    },

    fetchChapters() {
      const params = { type: 2, education_level: this.education_level };
      axios
        .get("/web/lessons", { params })
        .then((response) => {
          this.subscriptionLocked = false;
          this.chapters = response.data.data;
        })
        .catch((e) => { this.handleSubscriptionError(e); this.chapters = []; });
    },

    fetchLessons(chapterId) {
      axios
        .get(`/web/lessons?type=3&education_level=${this.education_level}&parent_id=${chapterId}`)
        .then((response) => {
          this.subscriptionLocked = false;
          this.lessons = response.data.data.map((l) => ({ ...l, contents: [] }));
        })
        .catch((e) => { this.handleSubscriptionError(e); this.lessons = []; });
    },

    async fetchContents(lessonId) {
      try {
        const params = { education_level: this.education_level, lesson_id: lessonId };
        const response = await axios.get("/web/contents", { params });
        const lesson = this.lessons.find((l) => l.id === lessonId);
        if (lesson) lesson.contents = response.data.data;
        await nextTick();
      } catch (e) {
        this.handleSubscriptionError(e);
        const lesson = this.lessons.find((l) => l.id === lessonId);
        if (lesson) lesson.contents = [];
      }
    },

    handleChapterToggle(chapterId, event) {
      const details = event.target;
      if (details.open && this.openedChapterId !== chapterId) {
        this.openedChapterId = chapterId;
        this.openedLessonId = null;
        this.lessons = [];
        this.fetchLessons(chapterId);
        if (this.isMobile) this.sidebarOpen = true;
      } else if (!details.open) {
        this.openedChapterId = null;
        this.openedLessonId = null;
        this.lessons = [];
      }
    },

    handleLessonToggle(lessonId) {
      if (this.openedLessonId !== lessonId) {
        this.openedLessonId = lessonId;
        this.fetchContents(lessonId);
      } else {
        this.openedLessonId = null;
      }
    },

    showContent(content) {
      this.selectedContentId = content.id;
      this.content = null;
      setTimeout(() => {
        this.content = content;
        if (content.type === "video" && content.link) {
          const match = content.link.match(/(?:v=|\/embed\/|\.be\/|watch\?v=)([A-Za-z0-9_-]{11})/);
          this.videoId = match && match[1] ? match[1] : "";
        } else {
          this.videoId = "";
        }
        if (this.isMobile) {
          this.sidebarOpen = false;
          nextTick(() => {
            if (this.$refs.viewer) {
              this.$refs.viewer.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          });
        }
      }, 10);
    },

    getContentTypeIcon(type) {
      if (type === "video") return "▶";
      if (type === "audio") return "🎵";
      if (type === "pdf")   return "📄";
      return "📁";
    },
  },
  mounted() {
    this.fetchAcademicYears();
    this.fetchChapters();
    this._onResize = () => { this.isMobile = window.innerWidth <= 768; };
    window.addEventListener("resize", this._onResize);
  },
  beforeUnmount() { window.removeEventListener("resize", this._onResize); },
};
</script>

<template>
  <div class="contents-page">

    <!-- ── Page hero ── -->
    <div class="contents-hero">
      <div class="contents-hero-inner">
        <div class="contents-hero-badge">📚 محتوى الحصص</div>
        <h1 v-if="academicYearLabel" class="contents-hero-title">
          {{ academicYearLabel }}
        </h1>
        <p class="contents-hero-sub">اختر الباب لاستعراض الدروس والمحتوى</p>
      </div>
    </div>

    <div class="contents-body">

      <!-- ── Subscription locked (Feature 7): shown when the API returns 403
           subscription_required. Backend enforcement is authoritative — this
           is just the UI. ── -->
      <div v-if="subscriptionLocked" class="subscription-locked">
        <div class="subscription-locked-icon">🔒</div>
        <h2 class="subscription-locked-title">المحتوى مقفل</h2>
        <p class="subscription-locked-msg">{{ subscriptionMessage }}</p>
        <router-link to="/subscribe" class="subscription-locked-btn">الاشتراك الآن</router-link>
      </div>

      <template v-else>
      <!-- ── Chapter tabs ── -->
      <div class="branches-strip">
        <div
          v-for="chapter in chapters"
          :key="chapter.id"
          class="branch-item"
          :class="{ active: openedChapterId === chapter.id }"
          @click="handleChapterToggle(chapter.id, { target: { open: openedChapterId !== chapter.id } })"
        >
          <p>{{ chapter?.title }}</p>
          <span class="branch_desctiption">{{ chapter?.description }}</span>
        </div>

        <div v-if="!chapters.length" class="branches-empty">
          جاري تحميل الأبواب...
        </div>
      </div>

      <!-- ── Main content layout ── -->
      <div class="contents-layout">

        <!-- Sidebar toggle (tablet 768–960px only) -->
        <button class="sidebar-toggle-btn" @click="sidebarOpen = !sidebarOpen">
          {{ sidebarOpen ? "← إخفاء القائمة" : "☰ عرض القائمة" }}
        </button>

        <!-- ── Mobile visible lessons panel header ── -->
        <div v-if="isMobile" class="mobile-panel-header" @click="sidebarOpen = !sidebarOpen">
          <div class="panel-header-label">
            <i class="pi pi-list"></i>
            <span>الدروس</span>
            <span v-if="lessons.length" class="panel-badge">{{ lessons.length }}</span>
          </div>
          <div class="panel-header-action">
            <span class="panel-status-text">{{ sidebarOpen ? "إخفاء" : "عرض" }}</span>
            <i :class="['pi', sidebarOpen ? 'pi-chevron-up' : 'pi-chevron-down']"></i>
          </div>
        </div>

        <!-- ── Sidebar ── -->
        <aside class="contents-sidebar" :class="{ 'sidebar-open': sidebarOpen }">

          <!-- No chapter selected -->
          <div v-if="!openedChapterId" class="sidebar-placeholder">
            <div class="sidebar-placeholder-icon">👆</div>
            <p>اختر باباً من الأعلى لاستعراض الدروس</p>
          </div>

          <!-- Lessons accordion -->
          <div v-else-if="lessons.length">
            <details
              v-for="lesson in lessons"
              :key="lesson.id"
              :open="openedLessonId === lesson.id"
            >
              <summary
                :class="{ opened: openedLessonId === lesson.id }"
                @click.prevent="handleLessonToggle(lesson.id)"
              >
                <span class="lesson_head">
                  <span class="title">{{ lesson.title }}</span>
                  <span class="description" v-if="lesson.description">{{ lesson.description }}</span>
                </span>
              </summary>

              <!-- Content items -->
              <div class="content-items">
                <div
                  v-for="c in lesson.contents || []"
                  :key="c.id"
                  @click="showContent(c)"
                  :class="['content-item', { active: selectedContentId === c.id }]"
                >
                  <span class="content-item-title">{{ c.title }}</span>
                  <span class="file-icon">
                    <template v-if="c.type === 'video'"><youtubeIcon /></template>
                    <template v-else-if="c.type === 'audio'"><audio /></template>
                    <template v-else-if="c.type === 'pdf'"><pdf /></template>
                    <template v-else>📁</template>
                  </span>
                </div>
              </div>
            </details>
          </div>

          <!-- Loading lessons -->
          <div v-else-if="openedChapterId" class="sidebar-placeholder">
            <div class="sidebar-placeholder-icon loading-icon">⏳</div>
            <p>جاري تحميل الدروس...</p>
          </div>

        </aside>

        <!-- ── Content viewer ── -->
        <main class="contents-viewer" ref="viewer">

          <!-- Welcome state -->
          <div v-if="!content" class="viewer-welcome">
            <div class="welcome-icon">🎬</div>
            <h2>اختر محتوى لعرضه</h2>
            <p>اختر درساً من القائمة لمشاهدة الفيديو أو الاستماع للصوت أو قراءة الملف</p>
          </div>

          <!-- Content player -->
          <div v-else class="viewer-player">

            <div class="viewer-header">
              <div class="content-type-chip">
                {{ getContentTypeIcon(content.type) }}
                {{ content.type === "video" ? "فيديو" : content.type === "audio" ? "صوت" : content.type === "pdf" ? "ملف PDF" : "ملف" }}
              </div>
              <h2 class="content-title">{{ content?.title }}</h2>
              <p class="content-description" v-if="content?.description">{{ content?.description }}</p>
            </div>

            <!-- Video -->
            <div class="player-wrap" v-if="content.type === 'video'">
              <YoutubeVideo
                v-show="activePlayer === 'youtube'"
                :videoId="videoId"
                :type="content.type"
                :key="'yt-' + videoId"
                ref="youtube"
              />
              <HawamPlayer
                v-show="activePlayer === 'hawam'"
                :videoId="videoId"
                :type="content.type"
                :key="'hw-' + videoId"
                ref="hawam"
              />
              <div class="player-switch">
                <button @click="activePlayer = 'youtube'" :class="{ active: activePlayer === 'youtube' }">
                  ▶ المشغّل الأول
                </button>
                <button @click="activePlayer = 'hawam'" :class="{ active: activePlayer === 'hawam' }">
                  ▶ المشغّل الثاني
                </button>
              </div>
            </div>

            <!-- Audio -->
            <AudioPlayer
              v-if="content.type === 'audio'"
              :link="content?.file || ''"
              :key="content?.file"
            />

            <!-- PDF -->
            <div v-if="content.type === 'pdf'" class="pdf-wrap">
              <PdfLearningViewer
                :pdf-url="content?.file || ''"
                :content-id="content?.id"
                :read-only="false"
                :watermark-enabled="true"
                :watermark-lines="[user?.reservation_number]"
              />
            </div>

          </div>

        </main>

      </div>
      </template>
    </div>

  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.contents-page {
  background: #F9F9F9; direction: rtl;
  font-family: 'Cairo', sans-serif;
  min-height: calc(100vh - 70px);
  overflow-x: hidden;
}

/* ─── Hero ─── */
.contents-hero {
  background: linear-gradient(135deg,#051629 0%,#F7B424 100%);
  padding: 2.5rem 1.5rem;
  position: relative; overflow: hidden;
}
.contents-hero::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at 80% 50%,rgba(0,212,166,.15) 0%,transparent 60%);
}
.contents-hero-inner { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }
.contents-hero-badge {
  display: inline-block; background: rgba(255,255,255,.18);
  backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,.25);
  border-radius: 99px; padding: .3rem 1rem;
  font-size: .82rem; font-weight: 700; color: #fff; margin-bottom: .85rem;
}
.contents-hero-title { font-size: 2rem; font-weight: 800; color: #fff; margin-bottom: .4rem; }
.contents-hero-sub { color: rgba(255,255,255,.82); font-size: .92rem; }

/* ─── Body ─── */
.contents-body { max-width: 1200px; margin: 0 auto; padding: 1.5rem 1.25rem 3rem; }

/* ─── Subscription locked ─── */
.subscription-locked {
  text-align: center; padding: 3.5rem 1.5rem; margin: .5rem 0 2rem;
  background: #fff; border-radius: 20px;
  border: 1px solid rgba(5, 22, 41,.08);
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
}
.subscription-locked-icon { font-size: 3.5rem; margin-bottom: .6rem; }
.subscription-locked-title { font-size: 1.35rem; font-weight: 800; color: #1F2937; margin-bottom: .5rem; }
.subscription-locked-msg { font-size: .9rem; color: #6B7280; line-height: 1.7; margin-bottom: 1.25rem; max-width: 420px; margin-left: auto; margin-right: auto; }
.subscription-locked-btn {
  display: inline-block; text-decoration: none;
  background: linear-gradient(135deg,#051629,#F7B424); color: #fff;
  font-weight: 700; font-size: .92rem; padding: .65rem 1.6rem; border-radius: 12px;
  box-shadow: 0 8px 20px rgba(5, 22, 41,.28);
}

/* ─── Branch strip ─── */
.branches-strip {
  display: flex; align-items: center; gap: .65rem;
  flex-wrap: wrap; margin-bottom: 1.75rem; padding: .25rem 0;
}
.branch-item {
  padding: .55rem 1.4rem; border: 2px solid rgba(5, 22, 41,.14);
  background: #fff; border-radius: 99px; cursor: pointer;
  font-weight: 600; font-size: .88rem; color: #6B7280;
  transition: all .3s; box-shadow: 0 4px 12px rgba(0,0,0,.06);
  border-radius: 16px; padding: .3rem .6rem !important; display: flex; flex-direction: column;     
  align-items: center; justify-content: center; min-width: 170px
}
.branch-item p { margin: 0; }
.branch-item .branch_desctiption {font-size: .8rem; color: #9CA3AF; display: block; margin-top: .15rem;}
.branch-item:hover { border-color: #051629; color: #051629; transform: translateY(-2px); box-shadow: 0 12px 32px rgba(5, 22, 41,.25); }
.branch-item.active { background: linear-gradient(135deg,#051629,#F7B424); border-color: transparent; color: #fff; box-shadow: 0 12px 32px rgba(5, 22, 41,.3); }
.branches-empty { color: #9CA3AF; font-size: .88rem; }

/* ─── Layout ─── */
.contents-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1.25rem;
  align-items: start;
}
.sidebar-toggle-btn { display: none; }

/* Mobile panel header hidden on desktop */
.mobile-panel-header { display: none; }

/* ─── Sidebar ─── */
.contents-sidebar {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
  border: 1px solid rgba(5, 22, 41,.08);
  overflow: hidden; position: sticky; top: 80px;
  max-height: calc(100vh - 100px); overflow-y: auto;
}
.contents-sidebar::-webkit-scrollbar { width: 4px; }
.contents-sidebar::-webkit-scrollbar-track { background: transparent; }
.contents-sidebar::-webkit-scrollbar-thumb { background: rgba(5, 22, 41,.2); border-radius: 99px; }

.sidebar-placeholder {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 3rem 1.5rem; text-align: center;
  color: #9CA3AF; gap: .75rem;
}
.sidebar-placeholder-icon { font-size: 2.5rem; }
.sidebar-placeholder p { font-size: .88rem; line-height: 1.6; }

.contents-sidebar details { border-bottom: 1px solid rgba(5, 22, 41,.06); }
.contents-sidebar details:last-child { border-bottom: none; }
.contents-sidebar summary {
  padding: .9rem 1rem; cursor: pointer;
  font-weight: 600; font-size: .88rem;
  display: flex; justify-content: space-between; align-items: center;
  transition: all .25s; list-style: none;
  color: #1F2937; background: #fff;
}
.contents-sidebar summary::-webkit-details-marker { display: none; }
.contents-sidebar summary::after {
  content: '▾'; font-size: .9rem; color: #051629;
  transition: transform .25s; flex-shrink: 0; margin-right: .25rem;
}
.contents-sidebar details[open] > summary::after { transform: rotate(180deg); }
.contents-sidebar summary .lesson_head { display: flex; flex-direction: column; gap: .1rem; flex: 1; }
.contents-sidebar summary .title { font-size: .88rem; font-weight: 600; }
.contents-sidebar summary .description { font-size: .73rem; color: #9CA3AF; }
.contents-sidebar summary.opened,
.contents-sidebar summary:hover {
  background: linear-gradient(135deg,rgba(5, 22, 41,.05),rgba(247, 180, 36,.04));
  color: #051629;
}
.contents-sidebar summary.opened .title,
.contents-sidebar summary:hover .title { color: #051629; }

.contents-sidebar details details {
  background: rgba(5, 22, 41,.02);
  border-bottom: none;
}
.contents-sidebar details details summary {
  font-size: .83rem; padding: .7rem 1rem .7rem 1.5rem;
  border-bottom: 1px solid rgba(5, 22, 41,.05);
}

.content-items { padding: .35rem .5rem; background: rgba(245,247,251,.9); }
.content-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: .5rem .75rem; margin-bottom: .2rem;
  border-radius: 8px; cursor: pointer;
  transition: all .15s; font-size: .83rem; color: #6B7280;
  border: 1px solid transparent;
}
.content-item-title { flex: 1; }
.file-icon svg { width: 18px; height: 18px; }
.content-item:hover {
  background: rgba(5, 22, 41,.08); color: #051629;
  border-color: rgba(5, 22, 41,.14);
}
.content-item.active {
  background: linear-gradient(135deg,rgba(5, 22, 41,.1),rgba(247, 180, 36,.08));
  color: #051629; border-color: rgba(5, 22, 41,.2); font-weight: 600;
}

/* ─── Viewer ─── */
.contents-viewer {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
  border: 1px solid rgba(5, 22, 41,.08);
  min-height: 60vh; overflow: hidden;
}

.viewer-welcome {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 60vh; text-align: center; padding: 3rem; gap: .85rem; color: #6B7280;
}
.welcome-icon { font-size: 4rem; margin-bottom: .5rem; }
.viewer-welcome h2 { font-size: 1.35rem; font-weight: 700; color: #1F2937; }
.viewer-welcome p { font-size: .9rem; max-width: 380px; line-height: 1.7; }

.viewer-player { padding: 1.5rem; }
.viewer-header { margin-bottom: 1.25rem; }
.content-type-chip {
  display: inline-flex; align-items: center; gap: .4rem;
  background: rgba(5, 22, 41,.07); color: #051629;
  padding: .25rem .85rem; border-radius: 99px;
  font-size: .78rem; font-weight: 700; margin-bottom: .6rem;
}
.content-title { font-size: 1.35rem; font-weight: 700; color: #1F2937; margin-bottom: .35rem; padding-bottom: .5rem; border-bottom: 2px solid rgba(5, 22, 41,.1); }
.content-description { color: #6B7280; font-size: .88rem; margin-bottom: .75rem; }

.player-wrap { margin-bottom: 1rem; }

.pdf-wrap {
  display: flex; flex-direction: column; gap: 1rem;
  min-height: 75vh;
}

.player-switch { display: flex; gap: .5rem; margin-top: .75rem; flex-wrap: wrap; }
.player-switch button {
  background: transparent; border: 2px solid rgba(5, 22, 41,.25);
  color: #051629; padding: .45rem 1.1rem; border-radius: 99px;
  font-weight: 600; font-size: .85rem; cursor: pointer;
  transition: all .25s; font-family: 'Cairo', sans-serif;
}
.player-switch button:hover, .player-switch button.active {
  background: linear-gradient(135deg,#051629,#F7B424);
  border-color: transparent; color: #fff; box-shadow: 0 8px 20px rgba(5, 22, 41,.25);
}

/* ─── Responsive: tablet (960px) ─── */
@media(max-width: 960px) {
  .contents-layout {
    grid-template-columns: 1fr;
  }
  .sidebar-toggle-btn {
    display: flex; align-items: center; gap: .5rem;
    background: rgba(5, 22, 41,.08); border: 1.5px solid rgba(5, 22, 41,.18);
    color: #051629; padding: .55rem 1.25rem; border-radius: 99px;
    font-weight: 700; font-size: .88rem; cursor: pointer;
    transition: all .25s; font-family: 'Cairo', sans-serif;
    width: fit-content; margin-bottom: .5rem;
  }
  .sidebar-toggle-btn:hover { background: rgba(5, 22, 41,.14); }
  .contents-sidebar { position: relative; top: 0; max-height: none; display: none; }
  .contents-sidebar.sidebar-open { display: block; }
}

/* ─── Responsive: mobile (768px) ─── */
@media(max-width: 768px) {
  /* Hero: clean gradient header with rounded bottom corners */
  .contents-hero {
    padding: 1.25rem 1.15rem 1.5rem !important;
    background: linear-gradient(160deg, #051629 0%, #3059A2 55%, #F7B424 100%);
    border-radius: 0 0 22px 22px;
    box-shadow: 0 10px 30px rgba(5, 22, 41,.2);
  }
  .contents-hero-title { font-size: 1.45rem !important; line-height: 1.3; margin-bottom: .2rem; }
  .contents-hero-sub { font-size: .8rem !important; line-height: 1.5; }
  .contents-hero-badge { font-size: .72rem !important; padding: .22rem .75rem !important; margin-bottom: .5rem !important; }

  /* Bottom padding clears the bottom nav */
  .contents-body {
    padding: 0 0 80px !important;
  }

  /* Branch tabs → responsive cards (2 columns) */
  .branches-strip {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: .6rem;
    padding: .85rem .75rem 0 !important;
    margin-bottom: 0 !important;
  }
  .branch-item {
    display: flex; align-items: center; justify-content: center;
    text-align: center;
    min-height: 54px;
    padding: .7rem .6rem !important;
    font-size: .85rem !important;
    font-weight: 700;
    border-radius: 16px;
    border: 1.5px solid rgba(5, 22, 41,.1);
    box-shadow: 0 2px 10px rgba(17,24,39,.06);
  }
  .branch-item.active { box-shadow: 0 8px 22px rgba(5, 22, 41,.28); }
  .branches-empty { padding: .25rem .35rem; }

  /* Layout: single column with inner padding */
  .contents-layout {
    grid-template-columns: 1fr !important;
    gap: .65rem !important;
    padding: .75rem !important;
    min-width: 0 !important;
    width: 100% !important;
  }

  /* Hide the tablet toggle on mobile (replaced by panel header below) */
  .sidebar-toggle-btn { display: none !important; }

  /* ── Visible mobile panel header (replaces hidden FAB) ── */
  .mobile-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: .9rem 1.1rem;
    background: linear-gradient(135deg, rgba(5, 22, 41,.07), rgba(247, 180, 36,.05));
    border: 1.5px solid rgba(5, 22, 41,.18);
    border-radius: 14px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background .18s, transform .15s;
    box-shadow: 0 2px 10px rgba(5, 22, 41,.1);
    user-select: none;
  }
  .mobile-panel-header:active {
    transform: scale(0.98);
    background: rgba(5, 22, 41,.12);
  }

  .panel-header-label {
    display: flex; align-items: center; gap: .6rem;
    font-weight: 700; color: #051629; font-size: .95rem;
  }
  .panel-header-label .pi { font-size: 1rem; }
  .panel-badge {
    background: #051629; color: #fff;
    border-radius: 99px; font-size: .7rem; font-weight: 700;
    padding: .1rem .5rem; line-height: 1.6;
    min-width: 22px; text-align: center;
  }
  .panel-header-action {
    display: flex; align-items: center; gap: .35rem;
    color: #051629; font-size: .82rem; font-weight: 600;
  }
  .panel-header-action .pi { font-size: .9rem; transition: transform .25s; }

  /* ── Sidebar: inline in normal flow (no longer a fixed drawer) ── */
  .contents-sidebar {
    display: none !important;
    position: relative !important;
    top: auto !important; right: auto !important; bottom: auto !important;
    width: 100% !important; max-width: 100% !important;
    z-index: auto !important; overflow-y: visible !important;
    max-height: none !important; transform: none !important;
    transition: none !important;
    border-radius: 14px !important;
    box-shadow: 0 4px 16px rgba(0,0,0,.08) !important;
  }
  .contents-sidebar.sidebar-open { display: block !important; }

  /* Viewer */
  .contents-viewer {
    border-radius: 14px !important;
    min-height: 50vh !important;
    min-width: 0 !important;
    width: 100% !important;
  }
  .viewer-player { padding: .85rem !important; min-width: 0 !important; }
  .content-title { font-size: 1.1rem !important; }
  .pdf-wrap { min-height: 60vh !important; min-width: 0 !important; width: 100% !important; }

  /* Larger touch targets inside sidebar */
  .contents-sidebar summary { padding: 1rem 1rem !important; min-height: 52px; }
  .contents-sidebar details details summary { padding: .85rem 1rem .85rem 1.35rem !important; }
  .content-item { padding: .8rem 1rem !important; min-height: 48px; }
  .content-item-title { font-size: .88rem !important; }
}

/* Wider phones / small tablets: 4 branch cards per row */
@media(min-width: 480px) and (max-width: 768px) {
  .branches-strip { grid-template-columns: repeat(4, 1fr); }
}

@media(max-width: 600px) {
  .viewer-player { padding: .75rem !important; }
  .pdf-wrap { min-height: 52vh !important; }
}
</style>